// Lightweight wrapper around the Meta Pixel browser snippet. We inject the
// base loader lazily on first call and then expose typed helpers for the
// events we care about. The eventID parameter is what dedupes the browser
// event against the server-side CAPI event sent from the API.

type FbqFn = {
  (cmd: 'init', pixelId: string): void;
  (cmd: 'track', event: string, params?: Record<string, unknown>, options?: { eventID?: string }): void;
  (cmd: string, ...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[];
  loaded?: boolean;
  version?: string;
  push?: unknown;
};

declare global {
  interface Window {
    fbq?: FbqFn;
    _fbq?: FbqFn;
  }
}

const inittedPixels = new Set<string>();

// Carrega o loader do fbevents.js uma única vez. Múltiplas chamadas de
// `initMetaPixel(id)` só adicionam pixels adicionais · o script fica o mesmo.
function ensureLoader(): void {
  if (typeof window === 'undefined' || window.fbq) return;
  const fbq = function (...args: unknown[]) {
    if (fbq.callMethod) fbq.callMethod.apply(fbq, args);
    else (fbq.queue ??= []).push(args);
  } as FbqFn;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];
  window.fbq = fbq;
  if (!window._fbq) window._fbq = fbq;

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);
}

/** Inicializa o Pixel NexoFácil (VITE_META_PIXEL_ID) e dispara PageView. */
export function initMetaPixel(): void {
  const pixelId = import.meta.env.VITE_META_PIXEL_ID;
  if (!pixelId) {
    console.warn('[meta-pixel] VITE_META_PIXEL_ID is not set; pixel disabled');
    return;
  }
  initPixelById(pixelId);
}

/**
 * Adiciona um Pixel Meta ADICIONAL (não substitui o Nexo). Usado por brokers
 * que têm pixel próprio · `track` posterior dispara pra todos os pixels
 * inicializados na página.
 */
export function initBrokerPixel(pixelId: string | null | undefined): void {
  if (!pixelId) return;
  initPixelById(pixelId);
}

function initPixelById(pixelId: string): void {
  if (typeof window === 'undefined' || inittedPixels.has(pixelId)) return;
  ensureLoader();
  window.fbq?.('init', pixelId);
  window.fbq?.('track', 'PageView');
  inittedPixels.add(pixelId);
}

/** Fires a Lead pixel event with the eventID so CAPI server-side dedupes it. */
export function trackLeadWithDedup(eventId: string): void {
  window.fbq?.('track', 'Lead', {}, { eventID: eventId });
}
